import { createSlice } from '@reduxjs/toolkit';
import {
  getBudgetGroupSnapshot,
  getMonitorConfigs,
  getMonitorDetail,
  getMonitorEffectiveConfig,
} from 'features/admin-monitoring/config/async';
import { MonitoringConfigState } from 'features/admin-monitoring/config/types';
import { saveMonitorSettings } from 'features/admin-monitoring/dependencies/async';
import {
  fulfilled,
  initialRequestState,
  pending,
  rejected,
  requestReducer,
} from 'features/shared/request';

export const initialState: MonitoringConfigState = {
  monitor: { data: undefined, request: initialRequestState },
  configs: { data: undefined, request: initialRequestState },
  configsMonitorKey: undefined,
  effectiveConfig: { data: undefined, request: initialRequestState },
  snapshot: { data: undefined, request: initialRequestState },
  snapshotIncludesEnded: false,
};

const slice = createSlice({
  name: 'monitoringConfig',
  initialState,
  reducers: {
    reset: () => initialState,
    resetEffectiveConfig(state) {
      state.effectiveConfig = {
        data: undefined,
        request: initialRequestState,
      };
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getMonitorDetail.pending, (state, { meta }) => {
      state.monitor.data = undefined;
      state.monitor.request = requestReducer(
        state.monitor.request,
        pending({ meta })
      );
    });
    builder.addCase(getMonitorDetail.fulfilled, (state, { meta, payload }) => {
      if (state.monitor.request.id === meta.requestId) {
        state.monitor.data = payload;
        state.monitor.request = requestReducer(
          state.monitor.request,
          fulfilled({ meta })
        );
      }
    });
    builder.addCase(getMonitorDetail.rejected, (state, { meta }) => {
      state.monitor.request = requestReducer(
        state.monitor.request,
        rejected({ meta })
      );
    });

    builder.addCase(saveMonitorSettings.fulfilled, (state, { payload }) => {
      if (
        payload.monitor &&
        state.monitor.data?.monitorKey === payload.monitor.monitorKey
      ) {
        state.monitor.data = payload.monitor;
      }
    });

    builder.addCase(getMonitorConfigs.pending, (state, { meta }) => {
      // Stale-while-revalidate: keep the current rows while refetching the same
      // monitor (no flash on a post-save refetch); clear only when the pending
      // fetch targets a different monitor, so stale cross-monitor data never
      // renders.
      if (state.configsMonitorKey !== meta.arg) {
        state.configs.data = undefined;
      }

      state.configsMonitorKey = meta.arg;
      state.configs.request = requestReducer(
        state.configs.request,
        pending({ meta })
      );
    });
    builder.addCase(getMonitorConfigs.fulfilled, (state, { meta, payload }) => {
      if (state.configs.request.id === meta.requestId) {
        state.configs.data = payload;
        state.configs.request = requestReducer(
          state.configs.request,
          fulfilled({ meta })
        );
      }
    });
    builder.addCase(getMonitorConfigs.rejected, (state, { meta }) => {
      state.configs.request = requestReducer(
        state.configs.request,
        rejected({ meta })
      );
    });

    builder.addCase(getMonitorEffectiveConfig.pending, (state, { meta }) => {
      state.effectiveConfig.data = undefined;
      state.effectiveConfig.request = requestReducer(
        state.effectiveConfig.request,
        pending({ meta })
      );
    });
    builder.addCase(
      getMonitorEffectiveConfig.fulfilled,
      (state, { meta, payload }) => {
        if (state.effectiveConfig.request.id === meta.requestId) {
          state.effectiveConfig.data = payload;
          state.effectiveConfig.request = requestReducer(
            state.effectiveConfig.request,
            fulfilled({ meta })
          );
        }
      }
    );
    builder.addCase(getMonitorEffectiveConfig.rejected, (state, { meta }) => {
      state.effectiveConfig.request = requestReducer(
        state.effectiveConfig.request,
        rejected({ meta })
      );
    });

    builder.addCase(getBudgetGroupSnapshot.pending, (state, { meta }) => {
      state.snapshot.data = undefined;
      state.snapshotIncludesEnded = meta.arg.minEndDate === undefined;
      state.snapshot.request = requestReducer(
        state.snapshot.request,
        pending({ meta })
      );
    });
    builder.addCase(
      getBudgetGroupSnapshot.fulfilled,
      (state, { meta, payload }) => {
        if (state.snapshot.request.id === meta.requestId) {
          state.snapshot.data = payload;
          state.snapshot.request = requestReducer(
            state.snapshot.request,
            fulfilled({ meta })
          );
        }
      }
    );
    builder.addCase(getBudgetGroupSnapshot.rejected, (state, { meta }) => {
      state.snapshot.request = requestReducer(
        state.snapshot.request,
        rejected({ meta })
      );
    });
  },
});

export const { reset, resetEffectiveConfig } = slice.actions;

const { reducer } = slice;

export default reducer;
